<template>
  <base-drawer
    class="cancel-transaction-drawer"
    :show="show"
    :title="drawerTitle"
    width="480px"
    @close="onClose"
  >
    <drawer-section title="Freed time slots" icon="icon-calendar-60">
      <info-hint variant="danger" icon="icon-alert-circle-exc" class="mb-3">
        Cancelling this transaction opens up availability for every booking in it. Any amount paid must be refunded to the customer separately.
      </info-hint>
      <div v-if="!loading && activeBookings.length > 0" class="drawer-form mb-3">
        <div v-for="booking in activeBookings" :key="'B' + booking.id" class="pricing-row">
          <div class="pricing-row__info">
            <span class="pricing-row__name text-white">{{ booking.name }}</span>
            <small class="d-block text-white opacity-7">{{ bookingWhen(booking) }}</small>
            <small class="d-block text-white opacity-7">{{ booking.group_size }} {{ booking.group_size === 1 ? 'participant' : 'participants' }}</small>
          </div>
        </div>
      </div>
      <base-input label="Status for the freed up time slots">
        <el-select
          v-model="slotStatus"
          class="select-primary"
          placeholder="Status"
        >
          <el-option
            v-for="status in slotStatuses"
            :key="status.value"
            class="select-primary"
            :label="status.label"
            :value="status.value"
          />
        </el-select>
      </base-input>
    </drawer-section>

    <drawer-section v-if="loading" title="Transaction contents" icon="icon-notes">
      <p class="text-white opacity-7 mb-0">Checking bookings, waivers, items and gift cards...</p>
    </drawer-section>

    <drawer-section v-if="!loading && waiverCount > 0" title="Waivers" icon="icon-paper">
      <p class="text-white mb-2">This transaction has {{ waiverCount }} {{ waiverCount === 1 ? 'waiver' : 'waivers' }} attached.</p>
      <segmented-toggle v-model="waiverAction" variant="pills" :options="waiverOptions" />
      <small class="d-block mt-2 text-white opacity-7">{{ waiverHelp }}</small>
    </drawer-section>

    <drawer-section v-if="!loading && itemCount > 0" title="Items" icon="icon-bag-16">
      <div class="drawer-form mb-3">
        <div v-for="item in items" :key="'I' + item.id" class="pricing-row">
          <div class="pricing-row__info">
            <span class="pricing-row__name text-white">{{ item.name }}</span>
            <small v-if="item.is_custom" class="d-block text-white opacity-7">Custom item</small>
          </div>
          <div class="pricing-row__total text-white">x{{ item.quantity }}</div>
        </div>
      </div>
      <segmented-toggle v-model="itemAction" variant="pills" :options="itemOptions" />
      <small class="d-block mt-2 text-white opacity-7">{{ itemHelp }}</small>
    </drawer-section>

    <drawer-section v-if="!loading && giftCardCount > 0" title="Gift cards" icon="icon-gift-2">
      <div class="drawer-form mb-3">
        <div v-for="card in activeGiftCards" :key="'G' + card.id" class="pricing-row">
          <div class="pricing-row__info">
            <code-chip :code="String(card.code)" label="gift card code" />
            <small class="d-block mt-1 text-white opacity-7">{{ card.gift_card_type }}</small>
          </div>
          <div class="pricing-row__total text-white">{{ formatAmount(card.remaining) }}</div>
        </div>
      </div>
      <segmented-toggle v-model="giftCardAction" variant="pills" :options="giftCardOptions" />
      <small class="d-block mt-2 text-white opacity-7">{{ giftCardHelp }}</small>
    </drawer-section>

    <template #footer>
      <div class="footer-actions">
        <base-button type="secondary" size="sm" :disabled="submitting" @click="onClose">Keep transaction</base-button>
        <base-button type="primary" size="sm" :disabled="submitting || loading" @click="submit">
          <i v-if="submitting" class="el-icon-loading mr-1"></i>Cancel transaction
        </base-button>
      </div>
    </template>
  </base-drawer>
</template>

<script lang="ts">
export default { name: 'CancelTransactionDrawer' }
</script>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount, getCurrentInstance } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { dayOfWeek, formattedDate, time } from '@/plugins/dateFormatter'
import { formatCurrency, getGroupSettings } from '@/util/currencyFormat'

// Shared cancel flow for the transactions list and the transaction details page.
// Fetches the transaction on open to decide which choices to show, then sends
// every choice as a query flag on the cancel request.
const props = defineProps({
  show: { type: Boolean, default: false },
  transactionId: { type: [Number, String], default: null }
})

const emit = defineEmits(['update:show', 'close', 'cancelled'])

const { proxy } = getCurrentInstance() as any

const companyId = localStorage.getItem('companyId')
const companyGroupId = JSON.parse(localStorage.getItem('group') || '{}').id
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}`

const slotStatuses = [
  { value: 'available', label: 'Available' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'call_to_book', label: 'Call to book' }
]
const waiverOptions = [
  { value: 'keep', label: 'Keep waivers on file' },
  { value: 'delete', label: 'Delete waivers' }
]
const itemOptions = [
  { value: 'remove', label: 'Remove items' },
  { value: 'keep', label: 'Keep items' }
]
const giftCardOptions = [
  { value: 'keep', label: 'Keep gift cards active' },
  { value: 'deactivate', label: 'Deactivate gift cards' }
]

const loading = ref(false)
const submitting = ref(false)
const slotStatus = ref('available')
const waiverAction = ref('keep')
const itemAction = ref('remove')
const giftCardAction = ref('keep')
const waiverCount = ref(0)
const orderNumber = ref<string | number | null>(null)
const bookings = ref<any[]>([])
const items = ref<any[]>([])
const giftCards = ref<any[]>([])

const activeBookings = computed(() => bookings.value.filter((b: any) => b.status !== 'cancelled'))
const activeGiftCards = computed(() => giftCards.value.filter((g: any) => g.is_active === 1))
const drawerTitle = computed(() => orderNumber.value ? `Cancel transaction #${orderNumber.value}` : 'Cancel transaction')
const itemCount = computed(() => items.value.length)
const giftCardCount = computed(() => activeGiftCards.value.length)

const group = getGroupSettings()

function bookingWhen(booking: any) {
  const prefix = group.show_day_of_week ? dayOfWeek(booking.booking_date) + ', ' : ''
  const date = formattedDate(group.date_format, booking.booking_date) || ''
  if (booking.is_all_day) return prefix + date + ', ' + (booking.all_day_text || 'All day')
  const start = time(group.time_format, booking.start_time) || ''
  const end = time(group.time_format, booking.end_time) || ''
  return prefix + date + (start ? ', ' + start + (end ? ' to ' + end : '') : '')
}

function formatAmount(value: number | string) {
  return formatCurrency(value, {
    currency: group.currency,
    currencySymbol: '$',
    currencyFormat: group.currency_format,
    numberFormat: group.number_format
  })
}

const waiverHelp = computed(() => waiverAction.value === 'delete'
  ? 'Waivers are permanently removed. Time slots are clean for new bookings.'
  : 'Waivers remain attached to the time slots. Future bookings inherit them.')
const itemHelp = computed(() => itemAction.value === 'remove'
  ? 'Items are removed from the transaction and returned to inventory.'
  : 'Items stay on the transaction and their amounts remain in the total.')
const giftCardHelp = computed(() => giftCardAction.value === 'deactivate'
  ? 'Gift cards can no longer be redeemed. Balances are not refunded.'
  : 'Gift cards can still be redeemed.')

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

function resetChoices() {
  slotStatus.value = 'available'
  waiverAction.value = 'keep'
  itemAction.value = 'remove'
  giftCardAction.value = 'keep'
  waiverCount.value = 0
  orderNumber.value = null
  bookings.value = []
  items.value = []
  giftCards.value = []
}

async function loadCounts() {
  if (!props.transactionId) return
  loading.value = true
  try {
    const config = await authConfig()
    const response = await axios.get(`${baseUrl}/transactions/${props.transactionId}`, config)
    const waivers = response.data.waivers || []
    waiverCount.value = waivers.length
    orderNumber.value = response.data.transaction?.order_number ?? null
    bookings.value = response.data.bookings || []
    items.value = response.data.items || []
    giftCards.value = response.data.gift_cards || []
  } catch (err) {
    console.log(err)
    // Counts stay at zero; the cancel still works with the slot status alone.
  } finally {
    loading.value = false
  }
}

async function submit() {
  if (!props.transactionId || submitting.value) return
  submitting.value = true
  try {
    const config = await authConfig()
    const url = `${baseUrl}/transactions/${props.transactionId}`
      + `?new_status=${slotStatus.value}`
      + `&delete_waivers=${waiverAction.value === 'delete'}`
      + `&remove_items=${itemAction.value === 'remove'}`
      + `&deactivate_gift_cards=${giftCardAction.value === 'deactivate'}`
    await axios.delete(url, config)
    proxy.$notify({ type: 'success', message: 'Transaction has been cancelled', icon: 'tim-icons icon-check-2' })
    emit('cancelled')
    onClose()
  } catch (err: any) {
    console.log(err)
    const message = err?.response?.status === 401
      ? 'You do not have access to this feature. Reach out to your admin regarding your site permissions.'
      : 'Something went wrong'
    proxy.$notify({ type: 'danger', message, icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    submitting.value = false
  }
}

function onClose() {
  emit('update:show', false)
  emit('close')
}

function onEscKey(e: KeyboardEvent) {
  if (e.key === 'Escape') onClose()
}

watch(() => props.show, (visible) => {
  if (visible) {
    document.addEventListener('keydown', onEscKey)
    resetChoices()
    loadCounts()
  } else {
    document.removeEventListener('keydown', onEscKey)
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onEscKey)
})
</script>
